'use client';

import React, {
  memo, useMemo, useState
} from 'react';
import {
  Bar, BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis, YAxis
} from 'recharts';

import styles from '../explorer.module.scss';
import { QueryResult } from '../types';
import { classifyColumns } from '../utils/columnClassification';
import { CHART_COLORS } from '../utils/constants';
import { buildFrequencyMap, isNullish } from '../utils/dataUtils';
import { getImbalanceSuggestion, getMissingValueAdvice } from '../utils/mlAdvice';
import { computeCorrelation, computeMissingValues, computeOutliers } from '../utils/statistics';


type Props = {
  result: QueryResult | null;
};

function nullColorClass( pct: number ): string {
  if ( pct < 5 ) return styles.statsColorGreen;
  if ( pct <= 20 ) return styles.statsColorYellow;
  return styles.statsColorRed;
}

function FeatureStatsPanel( { result }: Props ) {
  const [ labelColumn, setLabelColumn ] = useState<string>( '' );

  const classified = useMemo( () => {
    if ( !result || !result.columns.length ) return new Map();
    return classifyColumns( result.columns, result.rows );
  }, [ result ] );

  const numericCols = useMemo(
    () => [ ...classified.entries() ].filter( ( [ , c ] ) => c === 'numeric' ).map( ( [ k ] ) => k ),
    [ classified ]
  );

  const categoricalCols = useMemo(
    () => [ ...classified.entries() ].filter( ( [ , c ] ) => c === 'categorical' ).map( ( [ k ] ) => k ),
    [ classified ]
  );

  // Missing values
  const missingData = useMemo(
    () => ( result ? computeMissingValues( result.columns, result.rows ) : [] ),
    [ result ]
  );

  // Correlation matrix
  const correlationMatrix = useMemo( () => {
    if ( !result || numericCols.length < 2 ) return null;
    const matrix: { row: string; col: string; value: number; n: number }[] = [];
    for ( const a of numericCols ) {
      for ( const b of numericCols ) {
        // Use paired values for correct correlation (pairwise complete observations)
        const pairs = result.rows
          .map( r => ( { va: r[a], vb: r[b] } ) )
          .filter( p => !isNullish( p.va ) && !isNullish( p.vb )
            && Number.isFinite( Number( p.va ) ) && Number.isFinite( Number( p.vb ) ) );
        const colAVals = pairs.map( p => Number( p.va ) );
        const colBVals = pairs.map( p => Number( p.vb ) );
        matrix.push( { row: a, col: b, value: computeCorrelation( colAVals, colBVals ), n: pairs.length } );
      }
    }
    return matrix;
  }, [ result, numericCols ] );

  // Outlier detection
  const outlierData = useMemo( () => {
    if ( !result ) return [];
    return numericCols
      .map( col => computeOutliers( col, result.rows ) )
      .filter( ( o ): o is NonNullable<typeof o> => o !== null );
  }, [ result, numericCols ] );

  // Class balance (categorical with <=20 distinct values)
  const classBalanceData = useMemo( () => {
    if ( !result ) return [];
    return categoricalCols
      .map( col => {
        const freqMap = buildFrequencyMap( result.rows.map( r => r[col] ) );
        if ( freqMap.size > 20 || freqMap.size === 0 ) return null;
        const entries = [ ...freqMap.entries() ]
          .sort( ( a, b ) => b[1] - a[1] )
          .map( ( [ value, count ] ) => ( { value, count } ) );
        return { column: col, entries };
      } )
      .filter( ( d ): d is NonNullable<typeof d> => d !== null );
  }, [ result, categoricalCols ] );

  // Label distribution
  const labelDistribution = useMemo( () => {
    if ( !result || !labelColumn ) return null;
    const freqMap = buildFrequencyMap( result.rows.map( r => r[labelColumn] ) );
    if ( freqMap.size === 0 ) return null;
    const entries = [ ...freqMap.entries() ]
      .sort( ( a, b ) => b[1] - a[1] )
      .map( ( [ value, count ] ) => ( { value, count } ) );
    // Use loop-based min/max to avoid stack overflow on large arrays
    let maxCount = 0;
    let minCount = Infinity;
    for ( const e of entries ) {
      if ( e.count > maxCount ) maxCount = e.count;
      if ( e.count < minCount ) minCount = e.count;
    }
    const imbalanceRatio = minCount > 0 ? maxCount / minCount : Infinity;

    // Sample rows per class
    const samples = new Map<string, Record<string, unknown>[]>();
    for ( const row of result.rows ) {
      const v = row[labelColumn];
      if ( isNullish( v ) ) continue;
      const key = String( v );
      if ( !samples.has( key ) ) samples.set( key, [] );
      const arr = samples.get( key )!;
      if ( arr.length < 3 ) arr.push( row );
    }

    return { entries, imbalanceRatio, samples };
  }, [ result, labelColumn ] );

  // Eligible label columns (categorical with <=20 distinct)
  const labelColumnOptions = useMemo( () => {
    return classBalanceData.map( d => d.column );
  }, [ classBalanceData ] );

  const otherCols = useMemo(
    () => ( result ? result.columns.filter( c => c !== labelColumn ).slice( 0, 5 ) : [] ),
    [ result, labelColumn ]
  );

  if ( !result || !result.rows.length ) {
    return (
      <div className={ styles.statsPanel__empty }>
        <span>Run a query to see statistics</span>
      </div>
    );
  }

  return (
    <div className={ styles.statsPanel } data-testid="feature-stats-panel">
      {/* Section 1: Missing Values */}
      <div className={ styles.statsPanel__section }>
        <div className={ styles.statsPanel__sectionTitle }>Missing Values Summary ({ result.rows.length.toLocaleString() } total rows)</div>
        <table className={ styles.statsPanel__table }>
          <thead>
            <tr>
              <th>Column</th>
              <th>Null Count</th>
              <th>Null %</th>
              <th>Recommendation</th>
            </tr>
          </thead>
          <tbody>
            { missingData.map( d => {
              const isNum = classified.get( d.column ) === 'numeric';
              const advice = getMissingValueAdvice( d.column, d.nullPercent, isNum, result.rows.length, d.nullCount );
              return (
                <tr key={ d.column }>
                  <td>{ d.column }</td>
                  <td>{ d.nullCount.toLocaleString() }</td>
                  <td className={ nullColorClass( d.nullPercent ) }>{ d.nullPercent.toFixed( 1 ) }%</td>
                  <td className={ styles.statsPanel__adviceCell }>{ advice || '—' }</td>
                </tr>
              );
            } ) }
          </tbody>
        </table>
      </div>

      {/* Section 2: Correlation Matrix */}
      { correlationMatrix && numericCols.length >= 2 && (
        <div className={ styles.statsPanel__section }>
          <div className={ styles.statsPanel__sectionTitle }>Pearson Correlation Matrix (pairwise complete observations)</div>
          <div className={ styles.statsPanel__correlationGrid }>
            <table className={ styles.statsPanel__table }>
              <thead>
                <tr>
                  <th></th>
                  { numericCols.map( c => <th key={ c }>{ c }</th> ) }
                </tr>
              </thead>
              <tbody>
                { numericCols.map( rowCol => (
                  <tr key={ rowCol }>
                    <td className={ styles.statsPanel__corrLabel }>{ rowCol }</td>
                    { numericCols.map( colCol => {
                      const isDiagonal = rowCol === colCol;
                      const entry = correlationMatrix.find( m => m.row === rowCol && m.col === colCol );
                      const val = entry?.value ?? 0;
                      const bg = isDiagonal
                        ? '#E8EAF0'
                        : val > 0
                          ? `rgba(0, 176, 80, ${ Math.abs( val ) * 0.6 })`
                          : val < 0
                            ? `rgba(219, 79, 79, ${ Math.abs( val ) * 0.6 })`
                            : 'transparent';
                      return (
                        <td
                          key={ colCol }
                          style={ { background: bg, textAlign: 'center', color: isDiagonal ? '#999' : undefined, cursor: isDiagonal ? 'default' : 'help' } }
                          title={ isDiagonal ? '' : `r=${ val.toFixed( 4 ) }, N=${ entry?.n ?? 0 } pairs` }
                        >
                          { isDiagonal ? '—' : val.toFixed( 2 ) }
                        </td>
                      );
                    } ) }
                  </tr>
                ) ) }
              </tbody>
            </table>
          </div>
        </div>
      ) }

      {/* Section 3: Outlier Detection */}
      { outlierData.length > 0 && (
        <div className={ styles.statsPanel__section }>
          <div className={ styles.statsPanel__sectionTitle }>Outlier Detection (IQR Method)</div>
          <table className={ styles.statsPanel__table }>
            <thead>
              <tr>
                <th>Column</th>
                <th>N</th>
                <th>Q1</th>
                <th>Median</th>
                <th>Q3</th>
                <th>IQR</th>
                <th>Lower</th>
                <th>Upper</th>
                <th>Outliers</th>
                <th>Outlier %</th>
                <th>Skewness</th>
              </tr>
            </thead>
            <tbody>
              { outlierData.map( d => {
                const skewWarn = Math.abs( d.skewness ) > 2;
                return (
                  <tr key={ d.column }>
                    <td>{ d.column }</td>
                    <td>{ d.n.toLocaleString() }</td>
                    <td>{ d.q1.toFixed( 2 ) }</td>
                    <td>{ d.median.toFixed( 2 ) }</td>
                    <td>{ d.q3.toFixed( 2 ) }</td>
                    <td>{ d.iqr.toFixed( 2 ) }</td>
                    <td>{ d.lower.toFixed( 2 ) }</td>
                    <td>{ d.upper.toFixed( 2 ) }</td>
                    <td>{ d.outlierCount.toLocaleString() }</td>
                    <td>{ d.outlierPct.toFixed( 1 ) }%</td>
                    <td
                      className={ skewWarn ? styles.statsPanel__skewWarn : undefined }
                      title={ skewWarn ? 'Highly skewed — IQR bounds may be unreliable. Consider log-transform or robust scaling.' : `Skewness: ${ d.skewness.toFixed( 3 ) }` }
                    >
                      { d.skewness.toFixed( 2 ) }{ skewWarn ? ' ⚠' : '' }
                    </td>
                  </tr>
                );
              } ) }
            </tbody>
          </table>
        </div>
      ) }

      {/* Section 4: Class Balance */}
      { classBalanceData.length > 0 && (
        <div className={ styles.statsPanel__section }>
          <div className={ styles.statsPanel__sectionTitle }>Class Balance</div>
          { classBalanceData.map( d => (
            <div key={ d.column } className={ styles.statsPanel__classBalanceBlock }>
              <div className={ styles.statsPanel__classBalanceLabel }>{ d.column }</div>
              <div className={ styles.statsPanel__chartContainer }>
                <ResponsiveContainer width="100%" height={ Math.max( 80, d.entries.length * 28 ) }>
                  <BarChart data={ d.entries } layout="vertical" margin={ { left: 60, right: 20, top: 4, bottom: 4 } }>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" tick={ { fontSize: 11 } } />
                    <YAxis dataKey="value" type="category" tick={ { fontSize: 11 } } width={ 60 } />
                    <Tooltip contentStyle={ { fontFamily: 'monospace', background: '#fff', fontSize: 11 } } />
                    <Bar dataKey="count">
                      { d.entries.map( ( _, i ) => (
                        <Cell key={ i } fill={ CHART_COLORS[i % CHART_COLORS.length] } />
                      ) ) }
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          ) ) }
        </div>
      ) }

      {/* Section 5: Label Distribution Analysis */}
      { labelColumnOptions.length > 0 && (
        <div className={ styles.statsPanel__section }>
          <div className={ styles.statsPanel__sectionTitle }>Label Distribution Analysis</div>
          <div className={ styles.statsPanel__labelSelect }>
            <label htmlFor="label-col-select">Select Label Column:</label>
            <select
              id="label-col-select"
              data-testid="label-column-select"
              className={ styles.statsPanel__select }
              value={ labelColumn }
              onChange={ e => setLabelColumn( e.target.value ) }
            >
              <option value="">-- Select --</option>
              { labelColumnOptions.map( c => (
                <option key={ c } value={ c }>{ c }</option>
              ) ) }
            </select>
          </div>

          { labelDistribution && (
            <div className={ styles.statsPanel__labelContent }>
              <div className={ styles.statsPanel__chartContainer }>
                <ResponsiveContainer width="100%" height={ Math.max( 100, labelDistribution.entries.length * 30 ) }>
                  <BarChart data={ labelDistribution.entries } layout="vertical" margin={ { left: 80, right: 20, top: 4, bottom: 4 } }>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" tick={ { fontSize: 11 } } />
                    <YAxis dataKey="value" type="category" tick={ { fontSize: 11 } } width={ 80 } />
                    <Tooltip contentStyle={ { fontFamily: 'monospace', background: '#fff', fontSize: 11 } } />
                    <Bar dataKey="count">
                      { labelDistribution.entries.map( ( _, i ) => (
                        <Cell key={ i } fill={ CHART_COLORS[i % CHART_COLORS.length] } />
                      ) ) }
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className={ styles.statsPanel__imbalanceInfo }>
                <strong>Imbalance Ratio:</strong> { labelDistribution.imbalanceRatio === Infinity ? 'N/A' : labelDistribution.imbalanceRatio.toFixed( 2 ) }
                { ' ' }({ labelDistribution.entries.length } classes, { result.rows.length.toLocaleString() } total rows)
              </div>
              { getImbalanceSuggestion(
                labelDistribution.imbalanceRatio,
                labelDistribution.entries.length,
                result.rows.length,
                labelDistribution.entries[labelDistribution.entries.length - 1]?.count ?? 0,
                labelDistribution.entries[0]?.count ?? 0,
                labelDistribution.entries.reduce( ( s, e ) => s + e.count, 0 )
              ).map( ( suggestion, i ) => (
                <div key={ i } className={ styles.statsPanel__suggestion }>
                  { suggestion }
                </div>
              ) ) }

              {/* Per-class sample preview */}
              <div className={ styles.statsPanel__samplePreview }>
                <div className={ styles.statsPanel__sectionTitle }>Per-Class Sample Preview</div>
                { labelDistribution.entries.map( entry => {
                  const sampleRows = labelDistribution.samples.get( entry.value ) || [];
                  return (
                    <div key={ entry.value } className={ styles.statsPanel__sampleBlock }>
                      <div className={ styles.statsPanel__sampleLabel }>
                        { entry.value } ({ entry.count } rows)
                      </div>
                      { sampleRows.length > 0 && (
                        <table className={ styles.statsPanel__table }>
                          <thead>
                            <tr>
                              { otherCols.map( c => <th key={ c }>{ c }</th> ) }
                            </tr>
                          </thead>
                          <tbody>
                            { sampleRows.map( ( row, ri ) => (
                              <tr key={ ri }>
                                { otherCols.map( c => (
                                  <td key={ c }>{ row[c] === null || row[c] === undefined ? 'NULL' : String( row[c] ) }</td>
                                ) ) }
                              </tr>
                            ) ) }
                          </tbody>
                        </table>
                      ) }
                    </div>
                  );
                } ) }
              </div>
            </div>
          ) }
        </div>
      ) }
    </div>
  );
}

export default memo( FeatureStatsPanel );
